import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Nav, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import GenericCrudPanel from "../../components/common/crud/GenericCrudPanel";
import DetailGrid from "../../components/common/DetailGrid";

const HR_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/hr`;
const USER_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/user`;

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "PERMANENT", label: "Permanent" },
  { value: "CONTRACT", label: "Contract" },
  { value: "INTERN", label: "Intern" },
  { value: "PROBATION", label: "Probation" },
];

const EMPLOYEE_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "ON_LEAVE", label: "On Leave" },
  { value: "RESIGNED", label: "Resigned" },
  { value: "TERMINATED", label: "Terminated" },
];

const ACTIVE_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];

const PAYROLL_STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "PROCESSED", label: "Processed" },
  { value: "PAID", label: "Paid" },
];

const ATTENDANCE_STATUS_OPTIONS = [
  { value: "PRESENT", label: "Present" },
  { value: "ABSENT", label: "Absent" },
  { value: "HALF_DAY", label: "Half Day" },
  { value: "ON_LEAVE", label: "On Leave" },
  { value: "HOLIDAY", label: "Holiday" },
  { value: "WEEK_OFF", label: "Week Off" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { value: "AADHAAR", label: "Aadhaar" },
  { value: "PAN", label: "PAN" },
  { value: "PASSPORT", label: "Passport" },
  { value: "RESUME", label: "Resume" },
  { value: "OFFER_LETTER", label: "Offer Letter" },
  { value: "EXPERIENCE_LETTER", label: "Experience Letter" },
  { value: "APPRAISAL_LETTER", label: "Appraisal Letter" },
];

const REVIEW_TYPE_OPTIONS = [
  { value: "PROBATION", label: "Probation" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "HALF_YEARLY", label: "Half Yearly" },
  { value: "ANNUAL", label: "Annual" },
];

const RATING_OPTIONS = [
  { value: "UNSATISFACTORY", label: "Unsatisfactory (1 star)" },
  { value: "BELOW_EXPECTATIONS", label: "Below Expectations (2 stars)" },
  { value: "MEETS_EXPECTATIONS", label: "Meets Expectations (3 stars)" },
  { value: "EXCEEDS_EXPECTATIONS", label: "Exceeds Expectations (4 stars)" },
  { value: "OUTSTANDING", label: "Outstanding (5 stars)" },
];

const RATING_STAR_COUNT = {
  UNSATISFACTORY: 1,
  BELOW_EXPECTATIONS: 2,
  MEETS_EXPECTATIONS: 3,
  EXCEEDS_EXPECTATIONS: 4,
  OUTSTANDING: 5,
};

const REVIEW_STATUS_BADGE_CLASS = {
  DRAFT: "bg-secondary",
  SUBMITTED: "bg-info text-dark",
  ACKNOWLEDGED: "bg-warning text-dark",
  COMPLETED: "bg-success",
};

const numberValue = (value) => Number(value) || 0;

const calculateSalaryTotals = (form) => {
  const totalGrossSalary = [
    "basic_salary",
    "dearness_allowance",
    "hra",
    "conveyance",
    "medical_expense",
    "special_allowance",
    "other_allowances",
  ].reduce((total, field) => total + numberValue(form[field]), 0);
  const totalDeductions = [
    "provident_fund",
    "professional_tax",
    "esic",
    "other_deductions",
  ].reduce((total, field) => total + numberValue(form[field]), 0);

  return {
    ...form,
    total_gross_salary: totalGrossSalary,
    total_deductions: totalDeductions,
    net_salary: totalGrossSalary - totalDeductions,
  };
};

// Score is on a 0-10 scale (per backend spec, e.g. 8.75); shown as a percentage for at-a-glance reading.
function scoreToPercent(score) {
  if (score === null || score === undefined || score === "") return null;
  const numeric = Number(score);
  if (Number.isNaN(numeric)) return null;
  return Math.round(numeric * 10 * 10) / 10;
}

function StarRating({ rating }) {
  if (!rating) return <span className="text-muted">-</span>;
  const count = RATING_STAR_COUNT[rating] || 0;
  return (
    <span title={rating.replace(/_/g, " ")}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={n <= count ? "fa fa-star text-warning" : "fa fa-star-o text-muted"}></i>
      ))}
    </span>
  );
}

function ReviewStatusBadge({ status }) {
  return <span className={`badge ${REVIEW_STATUS_BADGE_CLASS[status] || "bg-secondary"}`}>{status || "DRAFT"}</span>;
}

const PERSONAL_INFO_FIELDS = [
  { name: "employee_code", label: "Employee Code", required: true },
  { name: "department", label: "Department", type: "select", optionsKey: "departmentOptions" },
  { name: "designation", label: "Designation", type: "select", optionsKey: "designationOptions" },
  { name: "reporting_manager", label: "Reporting Manager", type: "select", optionsKey: "employeeOptions" },
  { name: "employment_type", label: "Employment Type", required: true, type: "select", optionsKey: "employmentTypeOptions" },
  { name: "status", label: "Status", required: true, type: "select", optionsKey: "employeeStatusOptions" },
  { name: "date_of_birth", label: "Date of Birth", type: "date" },
  { name: "date_of_joining", label: "Date of Joining", type: "date" },
  { name: "date_of_leaving", label: "Date of Leaving", type: "date" },
  { name: "gender", label: "Gender", type: "select", optionsKey: "genderOptions" },
  { name: "personal_email", label: "Personal Email", type: "email" },
  { name: "pan_number", label: "PAN", type: "text" },
  { name: "uan_number", label: "UAN", type: "text" },
  { name: "emergency_contact_name", label: "Emergency Contact Name" },
  { name: "emergency_contact_number", label: "Emergency Contact Number" },
  { name: "address_line_1", label: "Address Line 1" },
  { name: "address_line_2", label: "Address Line 2" },
  { name: "city", label: "City" },
  { name: "state", label: "State" },
  { name: "pincode", label: "Pincode" },
  { name: "country", label: "Country", type: "select", optionsKey: "countryOptions" },
];

const PERSONAL_INFO_INITIAL_FORM = PERSONAL_INFO_FIELDS.reduce((acc, field) => {
  acc[field.name] = field.name === "employment_type" ? "PERMANENT" : field.name === "status" ? "ACTIVE" : "";
  return acc;
}, {});

const TABS = [
  { key: "personalinfo", label: "Personal Info" },
  { key: "asset", label: "Asset" },
  { key: "attendance", label: "Attendance" },
  { key: "payroll", label: "Payroll" },
  { key: "performance", label: "Performance" },
  { key: "documents", label: "Documents" },
];

function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function StatusBadge({ status }) {
  if (!status) return <span className="badge bg-warning text-dark">Setup Pending</span>;
  if (status === "ACTIVE") return <span className="badge bg-success">Active</span>;
  if (status === "ON_LEAVE") return <span className="badge bg-info text-dark">On Leave</span>;
  if (status === "RESIGNED" || status === "TERMINATED") return <span className="badge bg-secondary">{status}</span>;
  return <span className="badge bg-secondary">{status}</span>;
}

function EmployeeDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [activeTab, setActiveTab] = useState("personalinfo");

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [performanceSummary, setPerformanceSummary] = useState(null);

  // Paroll state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({ month: "", year: new Date().getFullYear() });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [payslipLoadingId, setPayslipLoadingId] = useState(null);
  const reloadRef = useRef(() => {});


  const [options, setOptions] = useState({});
  const [showEditCanvas, setShowEditCanvas] = useState(false);
  const [editForm, setEditForm] = useState(PERSONAL_INFO_INITIAL_FORM);
  const [editSaving, setEditSaving] = useState(false);

  const handleDataFromTopNavBar = (permissiondata) => {
    setDataFromTopNavBar(permissiondata);
  };

  const loadUserAndEmployee = async () => {
    setLoading(true);
    try {
      const [usersRes, employeesRes] = await Promise.all([
        axios.get(`${USER_BASE}/user/`),
        axios.get(`${HR_BASE}/employee/`),
      ]);
      const foundUser = (usersRes.data || []).find((item) => String(item.id) === String(userId));
      const foundEmployee = (employeesRes.data || []).find((item) => String(item.user_data?.id ?? item.user) === String(userId));
      setUser(foundUser || null);
      setEmployee(foundEmployee || null);
      if (foundEmployee) {
        loadRecentActivity(foundEmployee.id);
        loadPerformanceSummary(foundEmployee.id);
      }
    } catch (err) {
      toast.error("Error loading employee.");
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [departments, designations, employees, countries] = await Promise.all([
        axios.get(`${HR_BASE}/department/`),
        axios.get(`${HR_BASE}/designation/`),
        axios.get(`${HR_BASE}/employee/`),
        axios.get(`${USER_BASE}/country/`),
      ]);
      setOptions({
        departmentOptions: (departments.data || []).map((item) => ({ value: item.id, label: item.name })),
        designationOptions: (designations.data || []).map((item) => ({ value: item.id, label: item.title })),
        employeeOptions: (employees.data || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })),
        countryOptions: (countries.data || []).map((item) => ({ value: item.id, label: item.long_name })),
        employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS,
        employeeStatusOptions: EMPLOYEE_STATUS_OPTIONS,
        genderOptions: GENDER_OPTIONS,
      });
    } catch (err) {
      toast.error("Error loading form options.");
    }
  };

  const loadRecentActivity = async (employeeId) => {
    try {
      const [attendanceRes, payrollRes, assignmentRes, performanceRes] = await Promise.all([
        axios.get(`${HR_BASE}/attendance/`),
        axios.get(`${HR_BASE}/payroll/`),
        axios.get(`${HR_BASE}/assetassignment/`),
        axios.get(`${HR_BASE}/performance-review/`),
      ]);
      const belongsToEmployee = (row) => String(row.employee_data?.id ?? row.employee) === String(employeeId);

      const items = [];
      const latestAttendance = (attendanceRes.data || [])
        .filter(belongsToEmployee)
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];
      if (latestAttendance) {
        items.push({ label: `Attendance marked - ${latestAttendance.status || "-"}`, date: latestAttendance.date || "" });
      }

      const latestPayroll = (payrollRes.data || [])
        .filter(belongsToEmployee)
        .sort((a, b) => (b.year - a.year) || (b.month - a.month))[0];
      if (latestPayroll) {
        items.push({ label: `Payroll ${latestPayroll.status?.toLowerCase() || "updated"} for ${latestPayroll.month}/${latestPayroll.year}`, date: `${latestPayroll.year}-${String(latestPayroll.month).padStart(2, "0")}-01` });
      }

      const latestAssignment = (assignmentRes.data || [])
        .filter(belongsToEmployee)
        .sort((a, b) => (b.assigned_on || "").localeCompare(a.assigned_on || ""))[0];
      if (latestAssignment) {
        items.push({ label: `Asset assigned - ${latestAssignment.asset_data?.asset_code || latestAssignment.asset_data?.asset_name || "-"}`, date: latestAssignment.assigned_on || "" });
      }

      const latestPerformance = (performanceRes.data || [])
        .filter(belongsToEmployee)
        .sort((a, b) => (b.review_date || "").localeCompare(a.review_date || ""))[0];
      if (latestPerformance) {
        items.push({ label: `Performance review - ${latestPerformance.status || "-"}`, date: latestPerformance.review_date || "" });
      }

      items.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setRecentActivity(items);
    } catch (err) {
      // Non-critical panel; fail silently.
    }
  };

  const loadPerformanceSummary = async (employeeId) => {
    try {
      const response = await axios.get(`${HR_BASE}/performance-review/summary/?employee=${employeeId}`);
      setPerformanceSummary(response.data);
    } catch (err) {
      // Non-critical panel; fail silently.
    }
  };

  useEffect(() => {
    loadUserAndEmployee();
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const openEditCanvas = () => {
    if (employee) {
      const prepared = {};
      PERSONAL_INFO_FIELDS.forEach((field) => {
        const value = employee[field.name];
        if (typeof value === "object" && value !== null && value.id) {
          prepared[field.name] = value.id;
        } else if (value === null || value === undefined) {
          prepared[field.name] = "";
        } else {
          prepared[field.name] = value;
        }
      });
      setEditForm(prepared);
    } else {
      setEditForm({ ...PERSONAL_INFO_INITIAL_FORM });
    }
    setShowEditCanvas(true);
  };

  const renderEditField = (field) => {
    if (field.type === "select") {
      return (
        <Form.Select
          value={editForm[field.name] ?? ""}
          onChange={(event) => setEditForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
        >
          <option value="">Select {field.label}</option>
          {(options[field.optionsKey] || []).map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </Form.Select>
      );
    }
    return (
      <Form.Control
        type={field.type || "text"}
        value={editForm[field.name] ?? ""}
        onChange={(event) => setEditForm((prev) => ({ ...prev, [field.name]: event.target.value }))}
      />
    );
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    const missing = PERSONAL_INFO_FIELDS.filter((field) => field.required && !editForm[field.name]);
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map((field) => field.label).join(", ")}`);
      return;
    }

    const payload = { ...editForm, user: userId };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === "") delete payload[key];
    });

    setEditSaving(true);
    try {
      const response = employee
        ? await axios.put(`${HR_BASE}/employee/${employee.id}/`, payload)
        : await axios.post(`${HR_BASE}/employee/`, payload);
      toast.success(employee ? "Employee details updated successfully." : "Employee record created successfully.");
      setEmployee(response.data);
      setShowEditCanvas(false);
      loadRecentActivity(response.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.detail || "Error saving employee details.");
    } finally {
      setEditSaving(false);
    }
  };

  const assetAssignmentModule = useMemo(() => ({
    key: `assetassignment-${employee?.id || "none"}`,
    title: "Asset Assignments",
    endpoint: "/assetassignment/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "hidden" },
      { name: "asset", label: "Asset", required: true, type: "select", optionsKey: "assetOptions" },
      { name: "assigned_on", label: "Assigned On", required: true, type: "date" },
      { name: "returned_on", label: "Returned On", type: "date" },
      { name: "condition_given", label: "Condition Given", required: true },
      { name: "condition_returned", label: "Condition Returned" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    columns: [
      { name: "Asset", selector: (row) => row.asset_data?.asset_code || "-", sortable: true },
      { name: "Asset Name", selector: (row) => row.asset_data?.asset_name || "-", sortable: true },
      { name: "Assigned On", selector: (row) => row.assigned_on || "-", sortable: true },
      { name: "Returned On", selector: (row) => row.returned_on || "-", sortable: true },
    ],
    initialForm: { employee: employee?.id || "", asset: "", assigned_on: "", returned_on: "", condition_given: "", condition_returned: "", remarks: "" },
  }), [employee?.id]);

  const attendanceModule = useMemo(() => ({
    key: `attendance-${employee?.id || "none"}`,
    title: "Attendance",
    endpoint: "/attendance/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "hidden" },
      { name: "date", label: "Date", required: true, type: "date" },
      { name: "check_in_time", label: "Check In Time", type: "datetime-local" },
      { name: "check_out_time", label: "Check Out Time", type: "datetime-local" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "attendanceStatusOptions" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    columns: [
      { name: "Date", selector: (row) => row.date || "-", sortable: true },
      { name: "Check In", selector: (row) => row.check_in_time || "-", sortable: true },
      { name: "Check Out", selector: (row) => row.check_out_time || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
    ],
    initialForm: { employee: employee?.id || "", date: "", check_in_time: "", check_out_time: "", status: "PRESENT", remarks: "" },
  }), [employee?.id]);

  const salaryStructureModule = useMemo(() => ({
    key: `salarystructure-${employee?.id || "none"}`,
    title: "Salary Structure",
    endpoint: "/salarystructure/",
    fields: [
      { name: "basic_salary", label: "Basic Salary", required: true, type: "number", step: "1" },
      { name: "dearness_allowance", label: "Dearness Allowance", type: "number", step: "1" },
      { name: "hra", label: "HRA", type: "number", step: "1" },
      { name: "conveyance", label: "Conveyance", type: "number", step: "1" },
      { name: "medical_expense", label: "Medical Expense", type: "number", step: "1" },
      { name: "special_allowance", label: "Special Allowance", type: "number", step: "1" },
      { name: "other_allowances", label: "Other Allowances", type: "number", step: "1" },
      { name: "provident_fund", label: "Provident Fund", type: "number", step: "1" },
      { name: "professional_tax", label: "Professional Tax", type: "number", step: "1" },
      { name: "esic", label: "ESIC", type: "number", step: "1" },
      { name: "other_deductions", label: "Other Deductions", type: "number", step: "1" },
      { name: "total_gross_salary", label: "Gross Salary", type: "number", step: "1", disabled: true },
      { name: "total_deductions", label: "Total Deductions", type: "number", step: "1", disabled: true },
      { name: "net_salary", label: "Net Salary", type: "number", step: "1", disabled: true },
      { name: "effective_from", label: "Effective From", required: true, type: "date" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "activeStatusOptions" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Basic Salary", selector: (row) => row.basic_salary ?? "-", sortable: true },
      { name: "Gross Salary", selector: (row) => row.total_gross_salary ?? "-", sortable: true },
      { name: "Total Deductions", selector: (row) => row.total_deductions ?? "-", sortable: true },
      { name: "Net Salary", selector: (row) => row.net_salary ?? "-", sortable: true },
      { name: "Effective From", selector: (row) => row.effective_from || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
    ],
    initialForm: {
      employee: employee?.id || "",
      basic_salary: "",
      dearness_allowance: "",
      conveyance: "",
      medical_expense: "",
      hra: "",
      special_allowance: "",
      other_allowances: "",
      provident_fund: "",
      professional_tax: "",
      esic: "",
      other_deductions: "",
      total_gross_salary: "",
      total_deductions: "",
      net_salary: "",
      effective_from: "",
      status: "ACTIVE",
    },
    calculateForm: calculateSalaryTotals,
  }), [employee?.id]);

  const payrollModule = useMemo(() => ({
    key: `payroll-${employee?.id || "none"}`,
    title: "Payroll",
    endpoint: "/payroll/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "hidden" },
      { name: "month", label: "Month", required: true, type: "number" },
      { name: "year", label: "Year", required: true, type: "number" },
      { name: "gross_salary", label: "Gross Salary", type: "number", step: "0.01" },
      { name: "total_deductions", label: "Total Deductions", type: "number", step: "0.01" },
      { name: "net_salary", label: "Net Salary", type: "number", step: "0.01" },
      { name: "days_present", label: "Days Present", type: "number" },
      { name: "days_on_leave", label: "Days On Leave", type: "number" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "payrollStatusOptions" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
   columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Month/Year", selector: (row) => `${row.month || "-"}/${row.year || "-"}`, sortable: true },
      { name: "Gross", selector: (row) => row.gross_salary ?? "-", sortable: true },
      { name: "Deductions", selector: (row) => row.total_deductions ?? "-", sortable: true },
      { name: "Net", selector: (row) => row.net_salary ?? "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
      {
        name: "Payslip",
        selector: (row) =>
          row.salaryslip ? (
            <a href={row.salaryslip} target="_blank" rel="noopener noreferrer">
              <i className="fa fa-file-pdf-o me-1"></i>
              View
            </a>
          ) : (
            "-"
          ),
      },
    ],
    initialForm: {
      employee: employee?.id || "",
      month: "",
      year: new Date().getFullYear(),
      gross_salary: "",
      total_deductions: "",
      net_salary: "",
      days_present: "",
      days_on_leave: "",
      status: "DRAFT",
      remarks: "",
    },
  }), [employee?.id]);

  const employeeDocumentModule = useMemo(() => ({
    key: `employeedocument-${employee?.id || "none"}`,
    title: "Documents",
    endpoint: "/employee-document/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "hidden" },
      { name: "document_type", label: "Document Type", type: "select", optionsKey: "documentTypeOptions" },
      { name: "file", label: "File", type: "file", maxSizeMB: 2.5 },
    ],
    columns: [
      { name: "Document Type", selector: (row) => row.document_type || "-", sortable: true },
      { name: "Uploaded On", selector: (row) => (row.uploaded_on ? new Date(row.uploaded_on).toLocaleString() : "-"), sortable: true },
      {
        name: "File",
        selector: (row) => (
          row.file ? (
            <a href={`${process.env.REACT_APP_BACKEND_API_URL}${row.file}`} target="_blank" rel="noreferrer">
              Download
            </a>
          ) : (
            "-"
          )
        ),
      },
    ],
    initialForm: { employee: employee?.id || "", document_type: "", file: null },
  }), [employee?.id]);

  const performanceReviewModule = useMemo(() => ({
    key: `performancereview-${employee?.id || "none"}`,
    title: "Performance Reviews",
    endpoint: "/performance-review/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "hidden" },
      // { name: "reviewer", label: "Reviewer", type: "select", optionsKey: "employeeOptions" },
      { name: "review_period_start", label: "Review Period Start", required: true, type: "date" },
      { name: "review_period_end", label: "Review Period End", required: true, type: "date" },
      { name: "review_type", label: "Review Type", type: "select", optionsKey: "reviewTypeOptions" },
      { name: "rating", label: "Rating", type: "select", optionsKey: "ratingOptions" },
      { name: "score", label: "Score (out of 10)", type: "number", step: "0.01" },
      { name: "goals", label: "Goals", type: "textarea" },
      { name: "achievements", label: "Achievements", type: "textarea" },
      { name: "areas_of_improvement", label: "Areas of Improvement", type: "textarea" },
      { name: "reviewer_comments", label: "Reviewer Comments", type: "textarea" },
    ],
    columns: [
      { name: "Period", selector: (row) => `${row.review_period_start || "-"} to ${row.review_period_end || "-"}`, sortable: true },
      { name: "Type", selector: (row) => row.review_type || "-", sortable: true },
      { name: "Rating", cell: (row) => <StarRating rating={row.rating} />, selector: (row) => row.rating || "-" },
      {
        name: "Score",
        selector: (row) => (row.score !== null && row.score !== undefined && row.score !== "" ? `${row.score}/10 (${scoreToPercent(row.score)}%)` : "-"),
        sortable: true,
      },
      { name: "Status", cell: (row) => <ReviewStatusBadge status={row.status} />, selector: (row) => row.status || "-" },
    ],
    initialForm: {
      employee: employee?.id || "",
      reviewer: "",
      review_period_start: "",
      review_period_end: "",
      review_type: "ANNUAL",
      rating: "",
      score: "",
      goals: "",
      achievements: "",
      areas_of_improvement: "",
      reviewer_comments: "",
    },
  }), [employee?.id]);

  const belongsToEmployee = (employeeId) => (row) => String(row.employee_data?.id ?? row.employee) === String(employeeId);

  const loadAssetOptions = async () => {
    const assets = await axios.get(`${HR_BASE}/asset/`);
    return { assetOptions: (assets.data || []).map((item) => ({ value: item.id, label: `${item.asset_code} - ${item.asset_name}` })) };
  };

  const loadAttendanceOptions = async () => ({ attendanceStatusOptions: ATTENDANCE_STATUS_OPTIONS });
  const loadSalaryOptions = async () => ({ activeStatusOptions: ACTIVE_STATUS_OPTIONS });
  const loadPayrollOptions = async () => ({ payrollStatusOptions: PAYROLL_STATUS_OPTIONS });
  const loadDocumentOptions = async () => ({ documentTypeOptions: DOCUMENT_TYPE_OPTIONS });

  const loadPerformanceOptions = async () => {
    const employees = await axios.get(`${HR_BASE}/employee/`);
    return {
      // employeeOptions: (employees.data || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })),
      reviewTypeOptions: REVIEW_TYPE_OPTIONS,
      ratingOptions: RATING_OPTIONS,
    };
  };

    const generatePayslip = async (row, reload) => {
    setPayslipLoadingId(row.id);
    try {
      await axios.post(`${HR_BASE}/payroll/${row.id}/generate_payslip/`);
      toast.success("Payslip generated.");
      reload();
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to generate the payslip.");
      } else {
        toast.error(err?.response?.data?.error || "Error generating payslip.");
      }
    } finally {
      setPayslipLoadingId(null);
    }
  };

    const markPaid = async (row, reload) => {
    try {
      await axios.put(`${HR_BASE}/payroll/${row.id}/markpaid/`);
      toast.success("Payroll marked as paid.");
      reload();
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to update payroll.");
      } else {
        toast.error(err?.response?.data?.error || "Error marking payroll as paid.");
      }
    }
  };

    const submitGenerate = async () => {
    generateForm.employee = employee?.id || "";
    if (!generateForm.month || !generateForm.year || !generateForm.employee) {
      toast.error("Month, year, and employee are required.");
      return;
    }
    setGenerateLoading(true);
    try {
      const response = await axios.post(`${HR_BASE}/payroll/generate_single/`, generateForm);
      toast.success(`${(response.data || []).length} payroll record generated.`);
      setShowGenerateModal(false);
      reloadRef.current();
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("You do not have permission to generate payroll.");
      } else {
        toast.error(err?.response?.data?.error || "Error generating payroll.");
      }
    } finally {
      setGenerateLoading(false);
    }
  };

  const runReviewAction = async (row, action, reload, payload) => {
    try {
      await axios.put(`${HR_BASE}/performance-review/${row.id}/${action}/`, payload || {});
      toast.success(`Review ${action === "submit" ? "submitted" : action === "acknowledge" ? "acknowledged" : "completed"} successfully.`);
      reload();
      if (employee) loadPerformanceSummary(employee.id);
    } catch (err) {
      toast.error(err?.response?.data?.error || `Error performing ${action} on review.`);
    }
  };

  const renderPayrollToolbarActions = (module, reload) => {
    return (
      <Button
        className="btn btn-sm btn-info border-0 px-3 mx-2"
        onClick={() => {
          reloadRef.current = reload;
          setGenerateForm({ month: "", year: new Date().getFullYear() });
          setShowGenerateModal(true);
        }}
      >
        <i className="fa fa-cogs me-2"></i>
        Generate Payroll
      </Button>
    );
  };

  const renderPayrollRowActions = (row, module, reload) => {
    return (
      <>
        {row.status !== "PAID" && (
          <Button className="btn btn-sm btn-success border-0 mx-1" title="Mark Paid" onClick={() => markPaid(row, reload)}>
            <i className="fa fa-check"></i>
          </Button>
        )}
        <Button
          className="btn btn-sm btn-info border-0 mx-1"
          title="Generate Payslip"
          disabled={payslipLoadingId === row.id}
          onClick={() => generatePayslip(row, reload)}
        >
          {payslipLoadingId === row.id ? (
            <Spinner as="span" animation="border" size="sm" />
          ) : (
            <i className="fa fa-file-pdf-o"></i>
          )}
        </Button>
      </>
    )
  };



  const renderPerformanceRowActions = (row, module, reload) => (
    <>
      {row.status === "DRAFT" ? (
        <Button className="btn btn-sm btn-info border-0 mx-1" title="Submit" onClick={() => runReviewAction(row, "submit", reload)}>
          <i className="fa fa-paper-plane"></i>
        </Button>
      ) : null}
      {row.status === "SUBMITTED" ? (
        <Button className="btn btn-sm btn-warning border-0 mx-1" title="Acknowledge" onClick={() => runReviewAction(row, "acknowledge", reload)}>
          <i className="fa fa-check"></i>
        </Button>
      ) : null}
      {row.status === "ACKNOWLEDGED" ? (
        <Button className="btn btn-sm btn-success border-0 mx-1" title="Complete" onClick={() => runReviewAction(row, "complete", reload)}>
          <i className="fa fa-flag-checkered"></i>
        </Button>
      ) : null}
    </>
  );

  if (loading) {
    return (
      <div className="page-content-bg">
        <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
        <SideNavbar sidebarCollapsed={menuDrawerShow} activeMenu="HR" activeSubMenu="EmployeeList" activeSubMenuLevel1="" permissionFromTopNavbar={dataFromTopNavBar} />
        <div className={menuDrawerShow ? "row main-content-collapsed text-center py-5" : "row main-content text-center py-5"}>
          <Spinner className="mx-auto text-inox-green" as="span" animation="border" size="lg" role="status" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page-content-bg">
        <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
        <SideNavbar sidebarCollapsed={menuDrawerShow} activeMenu="HR" activeSubMenu="EmployeeList" activeSubMenuLevel1="" permissionFromTopNavbar={dataFromTopNavBar} />
        <div className={menuDrawerShow ? "row main-content-collapsed text-center py-5" : "row main-content text-center py-5"}>
          <p>Employee not found.</p>
        </div>
        <ToastContainer />
      </div>
    );
  }

  const displayName = user.name || user.email || user.username || "-";

  return (
    <div className="page-content-bg">
      <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="HR"
        activeSubMenu="EmployeeList"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div className="modal fade show" style={{ display: showGenerateModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Generate Payroll</h5>
              <button type="button" className="btn-close" onClick={() => setShowGenerateModal(false)}></button>
            </div>
            <div className="modal-body">
              <Form.Label>Month *</Form.Label>
              <Form.Control type="number" min="1" max="12" value={generateForm.month} onChange={(e) => setGenerateForm((prev) => ({ ...prev, month: e.target.value }))} />
              <Form.Label className="mt-2">Year *</Form.Label>
              <Form.Control type="number" value={generateForm.year} onChange={(e) => setGenerateForm((prev) => ({ ...prev, year: e.target.value }))} />
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>Close</Button>
              <Button className="btn-inox-green border-0" onClick={submitGenerate}>
                {generateLoading ? <Spinner as="span" animation="border" size="sm" /> : "Generate"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className={menuDrawerShow ? "row main-content-collapsed text-left py-0" : "row main-content text-left py-0"}>
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-sm-8 py-1">
              <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => setMenuDrawerShow(!menuDrawerShow)}>
                {menuDrawerShow ? <span><i className="fa fa-chevron-left mx-1"></i></span> : <span><i className="fa fa-chevron-right mx-1"></i></span>}
              </Button>
              <span className="page-header-text">
                <span className="pointer" onClick={() => navigate("/hr/employee")}>HR / Employees</span> / {displayName}
              </span>
            </div>
          </div>
        </div>

        <div className="col-12 text-left" style={{ marginTop: -5, paddingLeft: 20, paddingRight: 30 }}>
          <Button variant="link" className="px-0 mb-3 text-inox-green" onClick={() => navigate("/hr/employee")}>
            <i className="fa fa-arrow-left me-2"></i>Back to Employees
          </Button>

          <div className="border rounded p-4 mb-3 bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap">
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle bg-inox-green text-white d-flex align-items-center justify-content-center me-3"
                  style={{ width: 56, height: 56, fontSize: 20, fontWeight: 600, backgroundColor: "#2e7d32" }}
                >
                  {initials(displayName)}
                </div>
                <div>
                  <h5 className="mb-1">{displayName}</h5>
                  <div className="text-muted">
                    {employee?.designation_data?.title || "No designation set"} &bull; {user.email || "-"}
                  </div>
                </div>
              </div>
              <StatusBadge status={employee?.status} />
            </div>

            <Nav variant="tabs" className="mt-4 mb-3">
              {TABS.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link className="text-inox-green" active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            {activeTab === "personalinfo" ? (
              <div>
                <div className="d-flex justify-content-end mb-2">
                  <Button className="btn btn-sm btn-inox-green border-0" onClick={openEditCanvas}>
                    <i className="fa fa-edit me-2"></i>
                    {employee ? "Edit Details" : "Set Up Employee Details"}
                  </Button>
                </div>
                {employee ? (
                  <DetailGrid
                    sections={[
                      {
                        title: "Employment Details",
                        fields: [
                          { label: "Employee Code", value: employee.employee_code, icon: "fa-id-badge", mono: true },
                          { label: "Department", value: employee.department_data?.name, icon: "fa-building" },
                          { label: "Designation", value: employee.designation_data?.title, icon: "fa-briefcase" },
                          { label: "Employment Type", value: employee.employment_type, icon: "fa-file-text" },
                          { label: "Date of Joining", value: employee.date_of_joining, icon: "fa-calendar-check-o" },
                          { label: "Reporting Manager", value: employee.reporting_manager_data?.employee_code, icon: "fa-sitemap" },
                          { label: "PAN", value: employee.pan_number, icon: "fa-credit-card" },
                          { label: "UAN", value: employee.uan_number, icon: "fa-credit-card" },
                        ],
                      },
                      {
                        title: "Personal Details",
                        fields: [
                          { label: "Date of Birth", value: employee.date_of_birth, icon: "fa-birthday-cake" },
                          { label: "Gender", value: employee.gender, icon: "fa-venus-mars" },
                          { label: "Personal Email", value: employee.personal_email, icon: "fa-envelope" },
                          { label: "Emergency Contact", value: employee.emergency_contact_name ? `${employee.emergency_contact_name} (${employee.emergency_contact_number || "-"})` : "", icon: "fa-phone" },
                          { label: "Address", value: [employee.address_line_1, employee.address_line_2, employee.city, employee.state, employee.pincode, employee.country_data?.long_name].filter(Boolean).join(", "), icon: "fa-map-marker", wide: true },
                        ],
                      },
                    ]}
                  />
                ) : (
                  <p className="text-muted py-4 text-center">No employee record set up yet for this user. Click "Set Up Employee Details" to add one.</p>
                )}
              </div>
            ) : null}

            {activeTab === "asset" ? (
              employee ? (
                <GenericCrudPanel module={assetAssignmentModule} baseUrl={HR_BASE} loadOptions={loadAssetOptions} filterRows={(rows) => rows.filter(belongsToEmployee(employee.id))} />
              ) : (
                <p className="text-muted py-4 text-center">Set up employee details first to manage asset assignments.</p>
              )
            ) : null}

            {activeTab === "attendance" ? (
              employee ? (
                <GenericCrudPanel module={attendanceModule} baseUrl={HR_BASE} loadOptions={loadAttendanceOptions} filterRows={(rows) => rows.filter(belongsToEmployee(employee.id))} />
              ) : (
                <p className="text-muted py-4 text-center">Set up employee details first to manage attendance.</p>
              )
            ) : null}

            {activeTab === "payroll" ? (
              employee ? (
                <div>
                  <GenericCrudPanel module={salaryStructureModule} baseUrl={HR_BASE} loadOptions={loadSalaryOptions} filterRows={(rows) => rows.filter(belongsToEmployee(employee.id))} />
                  <hr className="my-4" />
                  <GenericCrudPanel
                    module={payrollModule}
                    baseUrl={HR_BASE}
                    loadOptions={loadPayrollOptions}
                    extraToolbarActions={renderPayrollToolbarActions}
                    extraRowActions={renderPayrollRowActions}
                    extraRowActionsWidth="200px"
                    filterRows={(rows) => rows.filter(belongsToEmployee(employee.id))}
                  />
                </div>
              ) : (
                <p className="text-muted py-4 text-center">Set up employee details first to manage payroll.</p>
              )
            ) : null}

            {activeTab === "performance" ? (
              employee ? (
                <div>
                  {performanceSummary ? (
                    <div className="d-flex gap-4 flex-wrap mb-3">
                      <div><strong>Total Reviews:</strong> {performanceSummary.total_reviews ?? 0}</div>
                      <div>
                        <strong>Average Score:</strong>{" "}
                        {performanceSummary.average_score !== null && performanceSummary.average_score !== undefined
                          ? `${performanceSummary.average_score}/10 (${scoreToPercent(performanceSummary.average_score)}%)`
                          : "-"}
                      </div>
                    </div>
                  ) : null}
                  <GenericCrudPanel
                    module={performanceReviewModule}
                    baseUrl={HR_BASE}
                    loadOptions={loadPerformanceOptions}
                    extraRowActions={renderPerformanceRowActions}
                    filterRows={(rows) => rows.filter(belongsToEmployee(employee.id))}
                  />
                </div>
              ) : (
                <p className="text-muted py-4 text-center">Set up employee details first to manage performance reviews.</p>
              )
            ) : null}

            {activeTab === "documents" ? (
              employee ? (
                <GenericCrudPanel module={employeeDocumentModule} baseUrl={HR_BASE} loadOptions={loadDocumentOptions} filterRows={(rows) => rows.filter(belongsToEmployee(employee.id))} />
              ) : (
                <p className="text-muted py-4 text-center">Set up employee details first to manage documents.</p>
              )
            ) : null}
          </div>

          {employee ? (
            <div className="row mb-4">
              <div className="col-md-7 mb-3">
                <div className="border rounded p-3 bg-white h-100">
                  <h6 className="mb-3">Recent Activity</h6>
                  {recentActivity.length === 0 ? (
                    <p className="text-muted mb-0">No recent activity.</p>
                  ) : (
                    recentActivity.map((item, index) => (
                      <div key={index} className={index < recentActivity.length - 1 ? "d-flex justify-content-between border-bottom py-2" : "d-flex justify-content-between py-2"}>
                        <span>{item.label}</span>
                        <span className="text-muted">{item.date}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {/* <div className="col-md-5 mb-3">
                <div className="border rounded p-3 bg-white h-100">
                  <h6 className="mb-3">Quick Links</h6>
                  <div className="d-flex flex-column gap-2">
                    <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("attendance")}>View Attendance &rarr;</Button>
                    <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("payroll")}>View Payroll &rarr;</Button>
                    <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("asset")}>View Assets &rarr;</Button>
                    <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("performance")}>View Performance &rarr;</Button>
                    <Button variant="link" className="text-inox-green text-start px-0" onClick={() => setActiveTab("documents")}>View Documents &rarr;</Button>
                  </div>
                </div>
              </div> */}
            </div>
          ) : null}
        </div>
      </div>

      <div className={`offcanvas-backdrop ${showEditCanvas ? "show" : ""}`} style={{ display: showEditCanvas ? "block" : "none" }} />
      <div
        className={`offcanvas offcanvas-bottom ${showEditCanvas ? "show" : ""}`}
        tabIndex="-1"
        style={{ visibility: showEditCanvas ? "visible" : "hidden", height: "60%", overflowY: "auto" }}
      >
        <div className="offcanvas-header border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
          <h5 className="offcanvas-title mb-0">{employee ? "Edit Employee Details" : "Set Up Employee Details"}</h5>
          <Button className="btn btn-danger btn-sm" size="sm" onClick={() => setShowEditCanvas(false)}>
            <i className="fa fa-times text-white"></i>
          </Button>
        </div>
        <div className="offcanvas-body px-4 py-3 text-left">
          <Form onSubmit={saveEdit}>
            <div className="row">
              {PERSONAL_INFO_FIELDS.map((field) => (
                <div className="col-md-6 mb-3" key={field.name}>
                  <Form.Label>{field.label}{field.required ? <span className="text-danger"> *</span> : null}</Form.Label>
                  {renderEditField(field)}
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button variant="secondary" onClick={() => setShowEditCanvas(false)}>Close</Button>
              <Button type="submit" className="btn-inox-green border-0">
                {editSaving ? <Spinner as="span" animation="border" size="sm" /> : "Save"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}

export default EmployeeDetail;
